import React, { useRef, useState, useEffect } from 'react';
import { Card, Button, Form, Dimmer } from "tabler-react";
import Select from 'react-select';
import { useFetch } from '../../../lib/useFetch';
import PutObject from '../../../lib/putObject';
import { toBase64 } from '../../../lib/helpers';

const StoryEditModal = ({ recordId, closeMainModal, reload }) => {
  const { data, loading, handleChange } = useFetch(`stories/${recordId}`);
  const { data: countries, loading: countriesLoading } = useFetch(`countries`);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const { data: cities, loading: citiesLoading } = useFetch(`cities?countryId=${selectedCountry && selectedCountry.value}`);
  const [selectedCity, setSelectedCity] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [hasNewImage, setHasNewImage] = useState(false);
  const [removeImage, setRemoveImage] = useState(false);
  const photoFile = useRef(null);

  useEffect(() => {
    if (!loading && data) {
      if (data.countryId) {
        setSelectedCountry({ label: data.countryId.name, value: data.countryId['_id'] });
      }
    }
  }, [loading, data]);

  useEffect(() => {
    if (!citiesLoading && cities && data) {
      let filteredCities = cities.filter(city => city.name === data.cityId?.name);
      setSelectedCity(filteredCities.length === 1 ? { label: data.cityId.name, value: data.cityId['_id'] } : null);
    }
  }, [cities, citiesLoading, data]);

  const validateForm = () => {
    if (!selectedCountry) {
      setError('Please select a country');
      return false;
    }
    if (!selectedCity) {
      setError('Please select a city');
      return false;
    }
    if (!data?.title) {
      setError('Please enter a title');
      return false;
    }
    if (!data?.body) {
      setError('Please enter the story body');
      return false;
    }
    if (photoFile.current.files.length > 0 && !photoFile.current.files[0].type.includes('image')) {
      setError('Please select a valid image file');
      return false;
    }
    return true;
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file && file.type.includes('image')) {
      const preview = await toBase64(file);
      setImagePreview(preview);
      setHasNewImage(true);
      setRemoveImage(false);
      setError('');
    } else if (file) {
      setError('Please select a valid image file');
      setImagePreview(null);
      setHasNewImage(false);
    }
  };

  const handleDropZoneClick = () => {
    photoFile.current.click();
  };

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setImagePreview(null);
    setHasNewImage(false);
    setRemoveImage(true);
    photoFile.current.value = '';
    // Force reset the file input
    if (photoFile.current) {
      photoFile.current.type = '';
      photoFile.current.type = 'file';
    }
  };

  const saveEdit = async () => {
    setError('');
    if (!validateForm()) return;

    try {
      setSaveLoading(true);
      
      const editedStory = {
        _id: recordId,
        countryId: selectedCountry.value,
        cityId: selectedCity.value,
        title: data.title,
        body: data.body
      };

      if (removeImage) {
        editedStory.photo = '';
      } else if (hasNewImage && photoFile.current.files.length > 0) {
        editedStory.photo = await toBase64(photoFile.current.files[0]);
      }
      
      await PutObject('stories', editedStory);
      
      if (typeof reload === 'function') {
        reload();
      }
      closeMainModal();
    } catch (err) {
      setError('Failed to update story. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  // Get current image source for preview
  const getCurrentImageSrc = () => {
    if (removeImage) return null;
    if (hasNewImage && imagePreview) {
      return imagePreview;
    }
    return data?.photo;
  };

  return (
    <Card>
      <Dimmer active={loading} loader={loading}>
        <Card.Header>
          <Card.Title>Edit Story</Card.Title>
          <Card.Options>
            <Button size="sm" icon="x" onClick={closeMainModal} />
          </Card.Options>
        </Card.Header>
        {!loading && data && (
          <>
            <Card.Body>
              {error && <div className="alert alert-danger">{error}</div>}
              <Form onSubmit={(e) => { e.preventDefault(); saveEdit(); }}>
                <Form.FieldSet>
                  <Form.Label>Country *</Form.Label>
                  <Select
                    isLoading={countriesLoading}
                    value={selectedCountry}
                    onChange={(e) => {
                      setSelectedCountry(e);
                      setSelectedCity(null);
                    }}
                    options={countries && countries.map(country => {
                      return ({ label: country.name, value: country['_id'] });
                    })}
                    placeholder="Select a country"
                    isClearable
                  />

                  <Form.Label>City *</Form.Label>
                  <Select
                    isLoading={citiesLoading}
                    value={selectedCity}
                    onChange={setSelectedCity}
                    options={cities && cities.map(city => {
                      return ({ label: city.name, value: city['_id'] });
                    })}
                    placeholder="Select a city"
                    isClearable
                    isDisabled={!selectedCountry}
                  />

                  <Form.Input
                    name='title'
                    label='Title'
                    placeholder='Enter story title'
                    value={data.title || ''}
                    onChange={handleChange}
                    required
                  />

                  <Form.Textarea
                    name='body'
                    label='Body'
                    placeholder='Enter story content'
                    value={data.body || ''}
                    onChange={handleChange}
                    required
                    rows={5}
                  />

                  <Form.Group>
                    <Form.Label>Story Photo</Form.Label>
                    <input 
                      type='file' 
                      ref={photoFile}
                      accept="image/*"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    
                    {!getCurrentImageSrc() ? (
                      <div className="drop-zone" onClick={handleDropZoneClick}>
                        <div className="drop-zone-icon">
                          <svg 
                            width="48" 
                            height="48" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="#6c757d" 
                            strokeWidth="1.5" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          >
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                            <circle cx="8.5" cy="8.5" r="1.5"/>
                            <polyline points="21,15 16,10 5,21"/>
                          </svg>
                        </div>
                        <div className="drop-zone-text">
                          {removeImage ? 'Click here to upload new photo' : 'Click here to upload story photo'}
                        </div>
                        <div className="drop-zone-hint">JPG, PNG, GIF supported</div>
                      </div>
                    ) : (
                      <div className="image-preview-container">
                        <img 
                          src={getCurrentImageSrc()} 
                          alt="Story Preview" 
                          className="image-preview"
                          onClick={handleDropZoneClick}
                        />
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={handleRemoveImage}
                        >
                          ×
                        </button>
                        {!hasNewImage && data?.photo && !removeImage && (
                          <div className="image-note">Current photo shown. Click to change.</div>
                        )}
                        {hasNewImage && (
                          <div className="image-note">New photo selected. Click to change.</div>
                        )}
                      </div>
                    )}
                  </Form.Group>
                </Form.FieldSet>
              </Form>
            </Card.Body>
            <Card.Footer>
              <div className="modal-footer">
                <Button
                  color="primary"
                  size="sm"
                  className={`ms-2 ${saveLoading ? 'btn-loading' : ''}`}
                  onClick={saveEdit}
                  disabled={saveLoading}
                >
                  Save Changes
                </Button>
                <Button
                  color="secondary"
                  size="sm"
                  className="ms-2"
                  onClick={closeMainModal}
                  disabled={saveLoading}
                >
                  Cancel
                </Button>
              </div>
            </Card.Footer>
          </>
        )}
      </Dimmer>
    </Card>
  );
};

export default StoryEditModal;